import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService, JwtService } from '@okcargo-apps/services';
import { enGB, es } from 'date-fns/locale';
import { DateFnsConfigurationService } from 'ngx-date-fns';
import { BehaviorSubject, firstValueFrom, map, Observable, tap } from 'rxjs';
import { ECapabilities, getEmptyIAccount, IAccount, ICredentials } from '../domain/account.model';
import { transformToIAccount } from '../infrastructure/account-mapper';
import { AccountQueryService } from '../infrastructure/account-query.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly apiService = inject(ApiService);
  private readonly jwtService = inject(JwtService);
  private readonly accountQueryService = inject(AccountQueryService);
  private readonly translate = inject(TranslateService);
  private readonly dateFnsConfigurationService = inject(DateFnsConfigurationService);

  private readonly _changePasswordUrl = '/services/uaa/api/account/change-password';
  private readonly _changeLaguageUrl = '/services/uaa/api/account/change-language';

  private readonly _account$: BehaviorSubject<IAccount> = new BehaviorSubject(getEmptyIAccount());

  get account$(): Observable<IAccount> {
    return this._account$.asObservable();
  }

  public async checkUserAuthentication(): Promise<void> {
    try {
      const user = await this.getIdentity();
      this._account$.next(user);
      this.setDateFnsLocale(user.lang);
      this.jwtService.setUserDataReceived(true);
    } catch (error) {
      console.warn(error);
    }
  }

  public hasCapability$(capability: ECapabilities): Observable<boolean> {
    return this._account$.pipe(map((account) => account.capabilities.includes(capability)));
  }

  public get userCapabilities$(): Observable<ECapabilities[]> {
    return this._account$.pipe(map((account) => account.capabilities || []));
  }

  public async login(credentials: ICredentials): Promise<void> {
    console.log('login() in account service');
    await firstValueFrom(
      this.jwtService.login$(credentials).pipe(
        tap((response) => {
          this.jwtService.setAccessToken(response.accessToken);
          this.jwtService.setRefreshToken(response.refreshToken);
        }),
      ),
    );
    await this.checkUserAuthentication();
  }

  public async validateToken(token: string): Promise<void> {
    console.log('validateToken() in account service');
    await firstValueFrom(
      this.jwtService.validateToken$(token).pipe(
        tap((response) => {
          this.jwtService.setAccessToken(response.accessToken);
          this.jwtService.setRefreshToken(response.refreshToken);
        }),
      ),
    );
    await this.checkUserAuthentication();
  }

  public async logout(): Promise<void> {
    //await firstValueFrom(this.jwtService.logout$());
    this._account$.next(getEmptyIAccount());
    this.jwtService.clearTokens();
  }

  public saveLang(lang: string): Promise<ArrayBuffer> {
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
    this.setDateFnsLocale(lang);
    return firstValueFrom(
      this.apiService.post(
        this._changeLaguageUrl,
        {
          langKey: lang,
        },
        { observe: 'response' },
      ),
    );
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<ArrayBuffer> {
    return await firstValueFrom(
      this.apiService.post(
        this._changePasswordUrl,
        {
          currentPassword: currentPassword,
          newPassword: newPassword,
        },
        { observe: 'response' },
      ),
    );
  }

  private async getIdentity(): Promise<IAccount> {
    const account = this._account$.getValue();
    const accountResponse = await this.accountQueryService.findAccount();

    return transformToIAccount(accountResponse, account);
  }

  private setDateFnsLocale(lang: string): void {
    if (lang === 'es') {
      this.dateFnsConfigurationService.setLocale(es);
    } else {
      this.dateFnsConfigurationService.setLocale(enGB);
    }
  }
}
